import { Fragment, useEffect, useId, type ReactNode, type RefObject } from 'react'
import type {
  Shell,
  FormDefinition,
  FormFieldState,
  FormSectionDefinition,
  FormFieldDefinition,
  ShellFeld,
  ShellAnmerkungen,
  ShellFeldArt,
  ShellHinweise,
  ShellOption,
  ShellOptionGroup,
  ShellQuelle,
  ShellQuellen,
  ShellWert,
  ShellWerte,
  ShellFreitextAntwort,
} from '@/core/templates/types'
import { getAt } from '@/core/lib/path'
import type { FieldId } from '@/core/lib/result'
import { formatFieldError, type FieldErrors } from '@/core/lib/fieldError'
import {
  shellWennApplies,
  collectVisibleShellLeafFelder,
  collectAllShellLeafIds,
  shellFelder,
  isShellFeldEmpty,
  isBlankText,
  isZahlValuePresentAndInvalid,
  assertValidShell,
  vortextLabel,
  WHOLE_NUMBER,
  ZAHL_MAX,
  DATE_SHAPE,
  requiredValueEmpty,
} from './formModel'
import { TextField, type TextFieldInputMode } from './TextField'
import { Checkbox } from './Checkbox'
import { SelectField } from './Select'
import { DateField } from './DateField'
import {
  Callout,
  Checkbox as DsCheckbox,
  Paragraph,
  RadioMatrix,
  VisuallyHidden,
  Field,
  FieldError,
  FieldGroup,
  FormSection as DsFormSection,
  Radio,
  RadioGroup,
  SelectableLabel,
  Text as DsText,
  Textarea,
  Title,
} from 'admination-react'

export type FieldHint = NonNullable<FormFieldState['hint']>
export type FieldHints = ReadonlyMap<FieldId, FieldHint>
export type DateLimits = { minIso?: string; maxIso?: string }
export type DateFieldLimits = ReadonlyMap<FieldId, DateLimits>
export type TextInputRule = { filter?: RegExp; inputMode?: TextFieldInputMode; maxLength?: number }
export type TextInputRules = ReadonlyMap<FieldId, TextInputRule>
export type FieldOverride = FormFieldState
export type FormState = {
  errors?: FieldErrors
  attemptedAdvance?: boolean
  readOnly?: boolean
  field?: (field: FormFieldDefinition) => FieldOverride | undefined
  section?: (id: string) => Partial<FormSectionDefinition>
}
export type FormRendererProps = FormState & {
  definition: FormDefinition
  values: Readonly<Record<string, unknown>>
  onChange: (id: string, value: unknown) => void
}
type ServerFormInput = {
  template: Shell
  values: ShellWerte
  onChange: (path: string, value: unknown) => void
  quellen?: ShellQuellen
  vorbelegung?: ShellWerte
  gehalteneWelche: RefObject<ShellWert | undefined>
  hinweise?: ShellHinweise
  anmerkungen?: ShellAnmerkungen
  serverErrors?: ReadonlyMap<FieldId, string>
  attemptedAdvance?: boolean
  fragen?: boolean
  frozenFields?: ReadonlySet<FieldId>
}

export function FormRenderer({ definition, values, onChange, ...state }: FormRendererProps) {
  function render(input: FormFieldDefinition): ReactNode {
    const override = state.field?.(input)
    const label = override?.label ?? input.label ?? input.id.slice(input.id.indexOf('.') + 1)
    const value =
      override && Object.hasOwn(override, 'value') ? override.value : Object.hasOwn(input, 'value') ? input.value : getAt(values, input.id)
    const error = state.errors?.get(input.id)
    const required = override?.required ?? input.required ?? false
    const manual = override?.missing === 'mark'
    const missing = input.validateRequired === true && requiredValueEmpty(value)
    const attempted = state.attemptedAdvance ?? true
    const base: FormFieldState = { ...input, options: undefined }
    const props = {
      ...base,
      ...override,
      id: input.id,
      label,
      required,
      invalid:
        override?.invalid ??
        input.invalid ??
        (!!override?.errorMessage || (!!error && !(manual && error.kind === 'missing' && !attempted)) || (missing && attempted)),
      errorMessage:
        override?.errorMessage ||
        input.errorMessage ||
        (error && !(manual && error.kind === 'missing')
          ? formatFieldError(label, error)
          : missing && attempted
            ? PFLICHTFELD_MESSAGE
            : undefined),
      readOnly: state.readOnly || override?.readOnly || input.readOnly || false,
      onChange: override?.onChange ?? input.onChange ?? ((next: unknown) => onChange(input.id, next)),
    }
    const text = typeof value === 'string' ? value : ''
    const prefix = input.prefix && (
      <ProseField {...input.prefix} text={input.prefix.value} onChange={(next) => onChange(input.prefix!.id, next)} />
    )
    let control: ReactNode
    switch (input.control) {
      case 'display':
        control = <TextField label={label} value={text} onChange={() => {}} readOnly />
        break
      case 'checkbox':
        control = <Checkbox label={label} checked={value === true} onChange={props.onChange} />
        break
      case 'select':
        control = <SelectField {...props} options={override?.options ?? input.options ?? []} value={text} readOnly={!!props.readOnly} />
        break
      case 'text-or-date':
      case 'date':
      case 'text':
        control =
          input.control === 'date' || (input.control === 'text-or-date' && DATE_SHAPE.test(text)) ? (
            <DateField {...props} isoValue={text} pastYears={override?.pastYears ?? input.pastYears ?? (/geburt/i.test(label) ? 120 : 0)} />
          ) : (
            <>
              {prefix}
              <TextField {...props} value={text} dictation={input.dictation ?? false} dictationContext={definition.title} />
            </>
          )
        break
      case 'number':
      case 'input':
        control = <InputField {...props} type={input.control === 'number' ? 'number' : (input.inputType ?? 'text')} value={value} />
        break
      case 'choice':
        control = (
          <ChoiceField
            {...props}
            options={input.options}
            presentation={input.presentation}
            fragen={input.fragen}
            value={(value ?? null) as ShellWert}
            vortext={prefix}
          />
        )
        break
      case 'multiple':
        control = (
          <MultipleField
            {...props}
            options={input.options}
            groups={input.groups}
            value={Array.isArray(value) ? value : []}
            vortext={prefix}
          />
        )
        break
      case 'heading': {
        const Heading = input.level ?? 'h4'
        control = <Heading className="section-subheader">{label}</Heading>
        break
      }
      case 'group':
        control = (
          <DsFormSection label={label} headingLevel="h4">
            {input.fields.map(field)}
          </DsFormSection>
        )
        break
      case 'matrix':
        control = <MatrixField {...props} options={input.options} rows={input.rows} value={value} />
        break
      case 'declaration':
        control = <DeclarationField {...props} checked={value === true} />
        break
    }
    return (
      <>
        {control}
        {props.warning && (
          <Callout tone="warning" role="status" data-testid={`feld-hinweis-${input.id}`}>
            {props.warning}
          </Callout>
        )}
        {props.annotation}
      </>
    )
  }
  const field = (input: FormFieldDefinition) => <Fragment key={input.id}>{render(input)}</Fragment>
  return (
    <>
      {Object.entries(definition.sections).map(([id, input]) => {
        const section = { ...input, ...state.section?.(id) }
        const content = (
          <>
            {section.text && <p className="section-note">{section.text}</p>}
            {section.fields.map(field)}
            {section.after && (
              <Paragraph variant="muted" id={section.after.id}>
                {section.after.text}
              </Paragraph>
            )}
          </>
        )
        return section.frame === 'none' ? (
          <Fragment key={section.id ?? id}>{content}</Fragment>
        ) : (
          <DsFormSection
            key={section.id ?? id}
            as={section.frame ?? 'div'}
            label={(section.heading ?? id) || undefined}
            compact={section.compact}
            headingLevel={section.level ?? 'h3'}
          >
            {content}
          </DsFormSection>
        )
      })}
    </>
  )
}

export function ServerForm(input: ServerFormInput) {
  const { template, values, onChange, quellen, vorbelegung, gehalteneWelche, hinweise, anmerkungen, serverErrors, frozenFields, fragen } = input
  const attempted = input.attemptedAdvance ?? true
  if (values == null) throw new Error(`FormRenderer: a shell template ("${template.titel}") requires \`values\``)
  assertValidShell(template)
  function fields(list: ShellFeld[] | undefined, level: 'h4' | 'h5'): FormFieldDefinition[] {
    return mitUnterTitel(list).flatMap<FormFieldDefinition>(({ feld, unterTitel }) => {
      if (!SHELL_FELD_ARTEN.has(feld.art)) throw new Error(`FormRenderer: unbekannte Feldart "${String(feld.art)}" bei Feld "${feld.id}"`)
      if (!shellWennApplies(feld.wenn, values, quellen)) return []
      const base = {
        id: feld.id,
        label: feld.label,
        value: values[feld.id],
        warning: hinweise?.[feld.id],
        annotation: anmerkungen?.[feld.id],
      }
      if (feld.art === 'zwischentitel') return [{ ...base, control: 'heading', level }]
      const ueberschrieben = feld.id === MASSNAHMEN_ANZAHL && values[feld.id] !== vorbelegung?.[feld.id]
      const hint = ueberschrieben ? undefined : shellFeldHint(feld, quellen)
      const prefix = feld.vortext && {
        id: feld.vortext.id,
        label: vortextLabel(feld),
        value: typeof values[feld.vortext.id] === 'string' ? (values[feld.vortext.id] as string) : '',
        readOnly: !!feld.nur_lesen,
        error: serverErrors?.get(feld.vortext.id),
        hint: shellFeldHint({ id: feld.vortext.id, label: '', art: 'mehrzeilig' }, quellen)?.text,
      }
      const serverError = serverErrors?.get(feld.id)
      if (feld.nur_lesen) {
        const missing = hinweise?.[feld.id] !== undefined
        return [
          {
            ...base,
            control: 'text',
            value: shellDisplayValue(values[feld.id]),
            onChange: () => {},
            readOnly: frozenFields?.has(feld.id) ? 'frozen' : true,
            required: missing && feld.pflicht,
            invalid: !!serverError || (missing && attempted),
            errorMessage: serverError,
            hint,
            prefix,
          },
        ]
      }
      if (feld.art === 'gruppe') {
        if (!feld.felder) throw new Error(`FormRenderer: "gruppe"-Feld "${feld.id}" hat keine \`felder\``)
        return [{ ...base, control: 'group', fields: fields(feld.felder, 'h5'), hint }]
      }
      const feedback = {
        ...base,
        required: feld.pflicht,
        invalid: shellFeldInvalid(feld, values[feld.id], serverError, attempted),
        errorMessage: shellFeldErrorMessage(feld, values[feld.id], serverError, attempted),
        hint,
        prefix,
      }
      switch (feld.art) {
        case 'text':
        case 'mehrzeilig':
          return [
            {
              ...feedback,
              control: 'text',
              label: feld.label || unterTitel || '',
              showLabel: feld.label !== '',
              dictation: feld.art === 'mehrzeilig',
            },
          ]
        case 'datum':
          return [{ ...feedback, control: 'date', pastYears: 0 }]
        case 'zahl':
          return [{ ...feedback, control: 'number' }]
        case 'auswahl':
          return [{ ...feedback, control: 'choice', options: feld.optionen, fragen }]
        case 'mehrfach':
          return [{ ...feedback, control: 'multiple', options: feld.optionen, groups: feld.gruppen }]
      }
    })
  }
  const definition: FormDefinition = {
    title: template.titel,
    sections: Object.fromEntries(
      template.abschnitte
        .filter((section) => (section.felder?.length ?? 0) > 0 || !!section.text)
        .map((section, index) => [
          index,
          { id: section.id, heading: section.titel || '', text: section.text, fields: fields(section.felder, 'h4') },
        ]),
    ),
  }
  useEffect(() => {
    const all = shellFelder(template)
    const visible = new Set(collectVisibleShellLeafFelder(all, values, quellen).map((field) => field.id))
    for (const id of collectAllShellLeafIds(all)) if (!visible.has(id) && values[id] !== undefined) onChange(id, undefined)
    const welche = values[MASSNAHMEN_WELCHE]
    if (!visible.has(MASSNAHMEN_WELCHE) && !isBlankText(welche)) gehalteneWelche.current = welche
    if (!visible.has(MASSNAHMEN_WELCHE) || welche !== undefined) return
    const zurueck = gehalteneWelche.current ?? (quellen?.[MASSNAHMEN_WELCHE] === 'verbis' ? vorbelegung?.[MASSNAHMEN_WELCHE] : undefined)
    if (zurueck !== undefined) onChange(MASSNAHMEN_WELCHE, zurueck)
  }, [template, values, quellen, vorbelegung, gehalteneWelche, onChange])
  return <FormRenderer definition={definition} values={values} onChange={onChange} />
}

function InputField({
  label,
  type,
  value,
  required,
  invalid,
  errorMessage,
  hint,
  disabled,
  size,
  requiredLabel,
  dir,
  autoComplete,
  placeholder,
  mark,
  markDescribed,
  descriptionId,
  onChange,
}: FormFieldState & { type: 'text' | 'tel' | 'email' | 'number' | 'time'; value: unknown; onChange: (next: unknown) => void }) {
  const id = useId()
  const generatedErrorId = useId()
  const errorId = size === 'lg' && errorMessage ? generatedErrorId : undefined
  return (
    <Field
      size={size}
      label={label}
      htmlFor={id}
      required={required}
      requiredLabel={requiredLabel}
      error={errorMessage}
      errorId={errorId}
      hint={hint?.text}
      hintTone={hint?.tone}
    >
      <DsText
        id={id}
        type={type}
        size={size}
        value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
        isFilled={size === 'lg' && String(value ?? '').trim() !== ''}
        disabled={disabled}
        dir={dir}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-describedby={[errorId ?? descriptionId, markDescribed && `${id}-mark`].filter(Boolean).join(' ') || undefined}
        markId={`${id}-mark`}
        mark={mark}
        onChange={(event) => {
          const raw = event.target.value
          onChange(type === 'number' ? (raw === '' ? null : WHOLE_NUMBER.test(raw) ? Number(raw) : raw) : raw)
        }}
      />
    </Field>
  )
}

function MatrixField({
  id,
  label,
  rows,
  options = [],
  optionLabel,
  rowLabel,
  value,
  disabled,
  invalid,
  errorMessage,
  rowInvalid,
  onChange,
}: FormFieldState & { id: string; rows: readonly string[]; value: unknown; onChange: (next: unknown) => void }) {
  const base = useId()
  const errorId = useId()
  const answers = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  return (
    <FieldGroup error={errorMessage} errorId={errorId}>
      <RadioMatrix aria-invalid={invalid || undefined}>
        <thead>
          <tr>
            <th scope="col">
              <VisuallyHidden>{label}</VisuallyHidden>
            </th>
            {options.map((option) => (
              <th scope="col" key={option}>
                {optionLabel?.(option) ?? option}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row}>
              <th scope="row">{rowLabel?.(row) ?? row}</th>
              {options.map((answer) => (
                <td key={answer}>
                  <Radio
                    name={`${base}-${row}`}
                    aria-label={`${rowLabel?.(row) ?? row}: ${optionLabel?.(answer) ?? answer}`}
                    aria-describedby={errorMessage && rowInvalid?.has(`${id}.${row}`) ? errorId : undefined}
                    checked={answers[row] === answer}
                    disabled={disabled}
                    onChange={() => onChange({ ...answers, [row]: answer })}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </RadioMatrix>
    </FieldGroup>
  )
}

function DeclarationField({
  label,
  checked,
  disabled,
  required,
  requiredLabel,
  invalid,
  errorMessage,
  onChange,
}: FormFieldState & { checked: boolean; onChange: (checked: boolean) => void }) {
  const errorId = useId()
  const checkbox = (
    <DsCheckbox
      multiline
      checked={checked}
      disabled={disabled}
      aria-required={required || undefined}
      aria-describedby={errorMessage ? errorId : undefined}
      onChange={(event) => onChange(event.target.checked)}
    >
      {label}
      {required ? (
        <Paragraph as="small" variant="muted">
          {' '}
          ({requiredLabel})
        </Paragraph>
      ) : null}
    </DsCheckbox>
  )
  return required ? (
    <FieldGroup error={errorMessage} errorId={errorId}>
      <div className="field" aria-invalid={invalid || undefined}>
        {checkbox}
      </div>
    </FieldGroup>
  ) : (
    checkbox
  )
}

function ProseField({
  label,
  text,
  readOnly,
  error,
  hint,
  onChange,
}: {
  label: string
  text: string
  readOnly: boolean
  error?: string
  hint?: string
  onChange: (text: string) => void
}) {
  if (readOnly && text === '') return null
  return (
    <>
      <Textarea
        rows={1}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        value={text}
        readOnly={readOnly}
        onChange={(event) => onChange(event.target.value)}
      />
      <FieldError>{error}</FieldError>
      {hint && <p className="field-hint is-info">{hint}</p>}
    </>
  )
}

const SHELL_FELD_ARTEN = new Set<ShellFeldArt>(['text', 'mehrzeilig', 'datum', 'zahl', 'auswahl', 'mehrfach', 'gruppe', 'zwischentitel'])

const MASSNAHMEN_ANZAHL = 'massnahmen_bisher_anzahl'
const MASSNAHMEN_WELCHE = 'massnahmen_bisher_welche'

const QUELLE_HINT: Record<ShellQuelle, string> = {
  zuweisungsliste: "aus der Zuweisungsliste",
  teilnahme: "aus der Teilnahme",
  verbis: "aus VerBIS",
  teilnehmerakte: "aus der Teilnehmerakte",
  berechnet: "berechnet",
  aufnahme: "aus A&P",
  vorgabe: "vorausgewählt",
  klett: "aus dem Klett-Test",
  verbis_ohne_ergebnis: "aus VerBIS: besucht, Ergebnis nicht angegeben",
  sprachlehrkraft: "aus dem Bewertungsbogen Sprachlehrkraft",
}

function shellFeldHint(feld: ShellFeld, quellen: ShellQuellen | undefined): FieldHint | undefined {
  const quelle = quellen?.[feld.id]
  if (quelle === undefined) return undefined
  if (typeof quelle !== 'string' || !Object.hasOwn(QUELLE_HINT, quelle)) {
    throw new Error(`FormRenderer: unbekannte Quelle "${String(quelle)}" bei Feld "${feld.id}"`)
  }
  return { tone: 'info', text: QUELLE_HINT[quelle] }
}

function shellFeldInvalid(feld: ShellFeld, value: ShellWert | undefined, serverError?: string, attemptedAdvance = true): boolean {
  if (serverError) return true
  if (feld.art === 'zahl' && isZahlValuePresentAndInvalid(value)) return true
  return attemptedAdvance && feld.pflicht === true && isShellFeldEmpty(feld, value)
}

const PFLICHTFELD_MESSAGE = "Pflichtfeld"

const TEXT_FEHLT_MESSAGE = "Text fehlt"

const FALSCHER_TYP_MESSAGE = "hat den falschen Typ"
const ZU_GROSS_MESSAGE = "zu groß"

function shellFeldErrorMessage(
  feld: ShellFeld,
  value: ShellWert | undefined,
  serverError?: string,
  attemptedAdvance = true,
): string | undefined {
  if (serverError) return serverError
  if (!shellFeldInvalid(feld, value, undefined, attemptedAdvance)) return undefined
  if (feld.art === 'zahl' && (typeof value === 'number' || typeof value === 'string')) {
    if (typeof value === 'string' && !WHOLE_NUMBER.test(value)) return FALSCHER_TYP_MESSAGE
    const n = typeof value === 'number' ? value : Number(value)
    return n > ZAHL_MAX ? ZU_GROSS_MESSAGE : FALSCHER_TYP_MESSAGE
  }
  return PFLICHTFELD_MESSAGE
}

function mitUnterTitel(felder: ShellFeld[] = []): { feld: ShellFeld; unterTitel?: string }[] {
  return felder.reduce<{ feld: ShellFeld; unterTitel?: string }[]>(
    (acc, feld) => [...acc, { feld, unterTitel: feld.art === 'zwischentitel' ? feld.label : acc[acc.length - 1]?.unterTitel }],
    [],
  )
}

function shellDisplayValue(value: ShellWert | undefined): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.map(shellAnswerText).join(', ')
  return shellAnswerText(value)
}

function shellAnswerText(entry: string | ShellFreitextAntwort): string {
  if (typeof entry === 'string') return entry
  return entry.text ? `${entry.label}: ${entry.text}` : entry.label
}

function optionLabelOf(option: ShellOption): string {
  return typeof option === 'string' ? option : option.label
}

function isFreitextOption(option: ShellOption): option is { label: string; freitext: true } {
  return typeof option !== 'string'
}

function shellSelectedLabel(value: ShellWert): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object' && !Array.isArray(value)) return value.label
  return ''
}

function ChoiceField({
  id,
  label: fieldLabel,
  options: optionen = [],
  required = false,
  value,
  onChange,
  invalid = false,
  errorMessage: gemeldet,
  hint,
  vortext,
  fragen = false,
  presentation,
  disabled,
  requiredLabel,
  ariaRequired,
  autoComplete,
  optionLabel,
}: {
  id: string
  label: string
  options?: ShellOption[]
  required?: boolean
  value: ShellWert
  onChange: (v: ShellWert) => void
  invalid?: boolean
  errorMessage?: string
  hint?: FieldHint
  vortext?: ReactNode
  fragen?: boolean
  presentation?: 'chips'
  disabled?: boolean
  requiredLabel?: ReactNode
  ariaRequired?: boolean
  autoComplete?: 'off'
  optionLabel?: (option: string) => string
}) {
  const name = useId()
  const errorId = useId()
  const chips = presentation === 'chips'
  const errorMessage = fragen && gemeldet === PFLICHTFELD_MESSAGE ? undefined : gemeldet
  const selectedLabel = shellSelectedLabel(value)
  const activeFreitextValue = value != null && typeof value === 'object' && !Array.isArray(value) ? value : null
  const freitextOption = optionen.find((o) => isFreitextOption(o) && optionLabelOf(o) === selectedLabel)

  function pick(label: string) {
    const opt = optionen.find((o) => optionLabelOf(o) === label)
    onChange(opt && isFreitextOption(opt) ? { label, text: '' } : label || null)
  }

  const asRow = optionen.length > 0 && optionen.length <= 5 && !optionen.some(isFreitextOption)
  const asFrage = chips || (fragen && asRow)
  const freitextTextInvalid = required === true && activeFreitextValue != null && isBlankText(activeFreitextValue.text)
  const selectInvalid = activeFreitextValue != null ? false : invalid
  const selectErrorMessage = activeFreitextValue != null ? undefined : errorMessage
  return (
    <>
      {asFrage ? (
        <>
          {vortext}
          <FieldGroup
            legend={fieldLabel}
            required={required}
            error={errorMessage}
            size={chips ? 'lg' : 'question'}
            requiredLabel={requiredLabel}
            errorId={chips ? errorId : undefined}
          >
            <RadioGroup
              stacked={!chips}
              role={chips ? 'radiogroup' : undefined}
              aria-invalid={invalid || undefined}
              aria-required={chips ? required || ariaRequired || undefined : undefined}
              aria-describedby={chips && errorMessage ? errorId : undefined}
            >
              {optionen.map((option) => {
                const label = optionLabelOf(option)
                return (
                  <Radio
                    key={label}
                    name={chips ? name : id}
                    chip={chips}
                    disabled={chips ? disabled : undefined}
                    autoComplete={autoComplete}
                    value={label}
                    checked={selectedLabel === label}
                    onChange={() => pick(label)}
                  >
                    {optionLabel?.(label) ?? label}
                  </Radio>
                )
              })}
            </RadioGroup>
          </FieldGroup>
          {hint?.text && <p className={`field-hint is-${hint.tone}`}>{hint.text}</p>}
        </>
      ) : asRow ? (
        <Field label={fieldLabel} required={required} error={errorMessage} hint={hint?.text} hintTone={hint?.tone}>
          {vortext}
          <div className="checkbox-row-group" aria-invalid={invalid || undefined}>
            {optionen.map((option) => {
              const label = optionLabelOf(option)
              return (
                <SelectableLabel key={label} isActive={selectedLabel === label} onClick={() => pick(label)}>
                  {label}
                </SelectableLabel>
              )
            })}
          </div>
        </Field>
      ) : (
        <>
          {vortext}
          <SelectField
            label={fieldLabel}
            options={optionen.map(optionLabelOf)}
            value={selectedLabel}
            onChange={pick}
            required={required}
            invalid={selectInvalid}
            errorMessage={selectErrorMessage}
            hint={hint}
          />
        </>
      )}
      {freitextOption && (
        <TextField
          label={optionLabelOf(freitextOption)}
          value={activeFreitextValue?.text ?? ''}
          onChange={(text) => onChange({ label: selectedLabel, text })}
          invalid={freitextTextInvalid}
          errorMessage={freitextTextInvalid ? TEXT_FEHLT_MESSAGE : undefined}
        />
      )}
    </>
  )
}

function MultipleField({
  label: fieldLabel,
  options,
  groups: optionGroups,
  required = false,
  value,
  onChange,
  invalid = false,
  errorMessage,
  hint,
  vortext,
}: {
  label: string
  options?: ShellOption[]
  groups?: ShellOptionGroup[]
  required?: boolean
  value: (string | ShellFreitextAntwort)[]
  onChange: (v: (string | ShellFreitextAntwort)[]) => void
  invalid?: boolean
  errorMessage?: string
  hint?: FieldHint
  vortext?: ReactNode
}) {
  const groups: ShellOptionGroup[] = optionGroups ?? [{ titel: '', optionen: options ?? [] }]
  const selectedLabels = new Set(value.map((v) => (typeof v === 'string' ? v : v.label)))

  function toggle(option: ShellOption, checked: boolean) {
    const label = optionLabelOf(option)
    if (checked) {
      const entry: string | ShellFreitextAntwort = isFreitextOption(option) ? { label, text: '' } : label
      onChange([...value, entry])
    } else {
      onChange(value.filter((v) => (typeof v === 'string' ? v : v.label) !== label))
    }
  }

  function setFreitext(label: string, text: string) {
    onChange(value.map((v) => (typeof v !== 'string' && v.label === label ? { label, text } : v)))
  }

  return (
    <Field label={fieldLabel} required={required} error={errorMessage} hint={hint?.text} hintTone={hint?.tone}>
      {vortext}
      {groups.map((group, gi) => (
        <div key={group.titel || gi}>
          {group.titel && <Title as="h4">{group.titel}</Title>}
          <div className="checkbox-grid" aria-invalid={invalid || undefined}>
            {group.optionen.map((option) => {
              const label = optionLabelOf(option)
              return <Checkbox key={label} label={label} checked={selectedLabels.has(label)} onChange={(v) => toggle(option, v)} />
            })}
          </div>
          {group.optionen.filter(isFreitextOption).map((option) => {
            const label = optionLabelOf(option)
            if (!selectedLabels.has(label)) return null
            const entry = value.find((v) => typeof v !== 'string' && v.label === label) as ShellFreitextAntwort | undefined
            const freitextTextInvalid = required === true && isBlankText(entry?.text)
            return (
              <TextField
                key={`${label}-text`}
                label={label}
                value={entry?.text ?? ''}
                onChange={(text) => setFreitext(label, text)}
                invalid={freitextTextInvalid}
                errorMessage={freitextTextInvalid ? TEXT_FEHLT_MESSAGE : undefined}
              />
            )
          })}
        </div>
      ))}
    </Field>
  )
}
